import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import uniqueId from 'lodash-es/uniqueId';
import { cloneDeep, set } from 'lodash-es';

import { getNumberInfo } from '@/page-components/utils/functions';
import { useAppSelector, useAppDispatch } from '../../../../store';
import { DataElementContext } from '../../../../page-components/common/DataElementContext';
import {
  momentumProgressDetails,
  starThresholds,
  detectBrowser,
  triggerGTMEvent,
} from '../../../../modules/momentum/utils/functions.js';
import {
  getMomentumBonuses,
  forfeitMomentumBonus,
  clearMomentumState,
  setMomentumSource,
  setMomentumPrizeShown,
} from '../../../../modules/momentum/store/actions/momentum';
import { exitSlotGame } from '../../../action-handler/actions';
import { formatCurrency } from '@/modules/bets/utils/formatters';
import { useMediaQuery } from '../../../utils/useQueryMedia';
import { setInGameHeaderSelection } from '@/modules/casino/store/actions/application';

import './index.scss';
import { FeatureCategory, FeatureSubcategory, logEvent, trackFunction } from '@/analytics';
import { FeatureLocation } from '@/analytics/types/eventData';

type MomentumGameHeaderProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);
const MomentumGameHeader = (componentProps: MomentumGameHeaderProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const prize = useAppSelector<any>((state) => state.momentum.prize?.value);
  const momentumStatus = useAppSelector<any>((state) => state.momentum.prize?.momentumDown);
  const momentumPrizeLoaded = useAppSelector<any>((state) => state.momentum?.prize?.loaded);
  const momentumPrizeLoading = useAppSelector<any>((state) => state.momentum?.prize?.loading);
  const source = useAppSelector<any>((state) => state.momentum.prize.source);
  const isDesktop = useMediaQuery('(min-width:900px)');
  const authentication = useAppSelector((state) => state.authentication);

  const initialStarProgress = momentumProgressDetails(prize?.wager_progress ?? 0, starThresholds);
  const isNoAnim = detectBrowser();

  const counterId = React.useRef(uniqueId('momentum-game-header-counter-'));
  const [state, setState] = React.useState({
    prize: prize,
    expired: false,
    showStartDialog: source === 'ws' && prize?.status !== 'wagered',
    showInfoDialog: false,
    showWonDialog: false,

    showLuckyStarAwardDialog: false,
    luckyStarExpiresAt: null,
    startLuckyStarCounter: false,

    showWarningDialog: false,
    showOverlay: false,
    showExpired: false,
    showWarning: false,
    progressAnimation: false,
    skipAnimation: false,
    starProgress: initialStarProgress.stars,
  });
  const tid = React.useRef<any>(0);
  const tidStart = React.useRef<any>(0);
  const tidExpired = React.useRef<any>(0);
  const currentValue = React.useRef(-1);
  const lerpStartTime = React.useRef(new Date().valueOf());

  const idleTime = 10000;
  const expiredTime = 10000;

  React.useEffect(() => {
    if (!momentumPrizeLoaded && ['user', 'token'].indexOf(authentication.auth_type) > -1) {
      dispatch(getMomentumBonuses());
    }
  }, [momentumPrizeLoaded, authentication, dispatch]);

  React.useEffect(() => {
    setState((v: any) => {
      let showWonDialog = prize?.status === 'wagered' && !v.showLuckyStarAwardDialog;
      let showStartDialog = source === 'ws' && !showWonDialog;
      let showExpired = prize?.status === 'expired';
      const showWarning = momentumStatus === true;

      const showLuckyStarAwardDialog =
        source === 'ws' && prize?.lucky_star_prize?.reward_type != null && !prize?.lucky_star_prize?.shown;
      if (showLuckyStarAwardDialog) {
        showStartDialog = false;
      }

      if (showLuckyStarAwardDialog && prize?.lucky_star_prize?.reward_type === 'INSTANT_COMPLETE') {
        showWonDialog = false;
      }

      let expired = false;

      if (prize?.bonus_expires_at) {
        const now = new Date().valueOf();
        const prizeExpiresAt = new Date(prize?.bonus_expires_at).valueOf();
        expired = now > prizeExpiresAt;
        if (expired) showExpired = true;
      }

      return {
        ...v,
        expired,
        prize,
        showWonDialog,
        showStartDialog,
        showLuckyStarAwardDialog,
        luckyStarExpiresAt: showLuckyStarAwardDialog ? new Date().valueOf() + expiredTime : null,
        showWarning,
        showExpired,
        showOverlay: false,
      };
    });

    if (tid.current) clearTimeout(tid.current);
    tid.current = setTimeout(() => {
      setState((v) => {
        let expired = false;

        if (prize?.bonus_expires_at) {
          const now = new Date().valueOf();
          const prizeExpiresAt = new Date(prize?.bonus_expires_at).valueOf();
          expired = now > prizeExpiresAt;
        }

        if (expired) {
          return v;
        }

        return {
          ...v,
          showOverlay: true,
        };
      });
    }, idleTime);
  }, [prize, source, momentumStatus]);

  const updateNumbers = React.useCallback((value: any, selector: any) => {
    let numberInfo = getNumberInfo({ amount: value, digits: 0 })?.amount;
    numberInfo = numberInfo.replace(/.00$/g, '');
    const elements = document.querySelectorAll(selector);
    elements.forEach((element: any) => {
      if (element) {
        element.innerHTML = numberInfo;
      }
    });
  }, []);

  const lerp = (x: number, y: number, a: number) => {
    return Math.floor(x * (1 - a) + y * a);
  };

  React.useEffect(() => {
    const selector = `#${counterId.current}`;
    if (prize?.lucky_star_prize?.reward_type === 'REWARD_INCREASE' && prize?.lucky_star_prize?.reward_value) {
      const finalValue = toFixedWithoutZeros(
        (prize?.reward?.value ?? 0) / 100 + prize?.lucky_star_prize?.reward_value,
        2,
      );
      updateNumbers(finalValue, selector);
    }
  }, []); // eslint-disable-line

  React.useEffect(() => {
    const selector = `#${counterId.current}`;
    let counterUpValue: any = 0;

    if (
      state.startLuckyStarCounter &&
      prize?.lucky_star_prize?.reward_type === 'REWARD_INCREASE' &&
      prize?.lucky_star_prize?.reward_value
    ) {
      currentValue.current = -1;
      counterUpValue = setInterval(() => {
        const startValue = prize?.reward?.value / 100;
        const finalValue = startValue + prize?.lucky_star_prize?.reward_value;

        if (currentValue.current === -1) {
          currentValue.current = startValue;
          lerpStartTime.current = new Date().valueOf();
          updateNumbers(currentValue.current, selector);
          return;
        }

        const start = lerpStartTime.current;
        const end = lerpStartTime.current + 2 * 1000;
        const now = new Date().valueOf();
        const delta = (now - start) / (end - start);

        //console.log('delta', delta);
        if (delta > 1) {
          currentValue.current = finalValue;
          updateNumbers(currentValue.current, selector);
          clearInterval(counterUpValue);
          setState((v) => ({
            ...v,
            startLuckyStarCounter: false,
          }));
          return;
        }
        currentValue.current = lerp(startValue, finalValue, delta);
        //console.log({ delta, startValue, finalValue, currentValue: currentValue.current });

        if (currentValue.current >= finalValue) {
          currentValue.current = finalValue;
          clearInterval(counterUpValue);
          setState((v) => ({
            ...v,
            startLuckyStarCounter: false,
          }));
        }

        updateNumbers(currentValue.current, selector);
      }, 50);
    }

    return () => clearInterval(counterUpValue);
  }, [state.startLuckyStarCounter, prize, counterId, updateNumbers]);

  React.useEffect(() => {
    if (state.showStartDialog) {
      triggerGTMEvent({ event: 'tw-intro-screen-displayed' });
      logEvent(
        null,
        {
          feature_used: 'turboCashPresentRewardNotification',
          feature_category: FeatureCategory.MomentumTurboCash,
          feature_subcategory: FeatureSubcategory.SystemEvent,
          feature_location: FeatureLocation.InGameNotification,
        },
        {
          event_name: 'tw-intro-screen-displayed',
          event_details: {
            state: state,
          },
        },
      );
      tidStart.current = setTimeout(() => {
        onCloseStartDialog();
        triggerGTMEvent({ event: 'tw-intro-screen-auto-dismissed' });
        logEvent(
          null,
          {
            feature_used: 'turboCashRewardAutoClose',
            feature_category: FeatureCategory.MomentumTurboCash,
            feature_subcategory: FeatureSubcategory.SystemEvent,
            feature_location: FeatureLocation.InGameNotification,
          },
          {
            event_name: 'tw-intro-screen-auto-dismissed',
            event_details: {
              state: state,
            },
          },
        );
      }, 5000);
    } else {
      clearTimeout(tidStart.current);
    }
  }, [state.showStartDialog]); // eslint-disable-line

  React.useEffect(() => {
    if (state.showWonDialog) {
      triggerGTMEvent({ event: 'tw-won-screen-displayed' });
      logEvent(
        null,
        {
          feature_used: 'turboCashPresentWinNotification',
          feature_category: FeatureCategory.MomentumTurboCash,
          feature_subcategory: FeatureSubcategory.SystemEvent,
          feature_location: FeatureLocation.InGameNotification,
        },
        {
          event_details: {
            state: state,
          },
        },
      );
    }
  }, [state, state.showWonDialog]);

  const handleExpired = React.useCallback(() => {
    setState((v) => ({
      ...v,
      expired: true,
      showExpired: true,
    }));
  }, []);

  React.useEffect(() => {
    if (state.showExpired) {
      if (tidExpired.current) clearTimeout(tidExpired.current);
      tidExpired.current = setTimeout(() => {
        dispatch(clearMomentumState(true));
      }, expiredTime);
      triggerGTMEvent({ event: 'tw-time-up' });
      logEvent(
        null,
        {
          feature_used: 'time-up',
          feature_category: FeatureCategory.MomentumTurboCash,
          feature_subcategory: FeatureSubcategory.SystemEvent,
          feature_location: FeatureLocation.InGameNotification,
        },
        {
          event_details: {
            progress: starProgress.progress,
            stars: starProgress.stars,
          },
        },
      );
    }

    return () => {
      if (tidExpired.current) clearTimeout(tidExpired.current);
    };
  }, [state.showExpired]); // eslint-disable-line

  const handleProgressAnimationEnd = (e: any) => {
    if (e?.target?.id !== 'star_travel_complet_v3_169x90-u-star_tr') return;

    const starProgress = momentumProgressDetails(state.prize?.wager_progress ?? 0, starThresholds);
    setState((v) => {
      if (starProgress.stars === v.starProgress) {
        return v;
      }

      let stars = starProgress.stars;
      if (stars - v.starProgress > 1) {
        stars = v.starProgress + 1;
      }

      return {
        ...v,
        progressAnimation: false,
        starProgress: stars,
      };
    });
    triggerGTMEvent({ event: 'tw-level-done' });
  };

  React.useEffect(() => {
    if (!state.skipAnimation) {
      const starProgress = momentumProgressDetails(state.prize?.wager_progress ?? 0, starThresholds);

      if (starProgress.stars !== state.starProgress && !state.progressAnimation) {
        setState((v) => ({
          ...v,
          progressAnimation: true,
        }));
        logEvent(
          null,
          {
            feature_used: 'turboCashStarLevelUp',
            feature_category: FeatureCategory.MomentumTurboCash,
            feature_subcategory: FeatureSubcategory.SystemEvent,
            feature_location: FeatureLocation.InGameNotification,
          },
          {
            event_details: {
              progress: starProgress.progress,
              stars: starProgress.stars,
            },
          },
        );
      }
      try {
        if (window.location.pathname.startsWith('/play/')) {
          const parts = window.location.pathname.split('/');
          const gameId = parseInt(parts[parts.length - 1], 10);
          localStorage.setItem('momentumGameId', gameId.toString());
        }
      } catch (e) {}
    }
  }, [state.skipAnimation, state.prize?.wager_progress, state.starProgress, state.progressAnimation]);

  const onShowInfoDialog = () => {
    setState((v) => {
      let showInfoDialog = true;
      let showWarningDialog = false;

      if (v.showWarning) {
        showInfoDialog = false;
        showWarningDialog = true;
      }

      return {
        ...v,
        showInfoDialog: showInfoDialog,
        showWarningDialog: showWarningDialog,
      };
    });
    triggerGTMEvent({ event: 'tw-info-click' });
  };
  const onCloseInfoDialog = () => {
    setState((v) => ({
      ...v,
      showInfoDialog: false,
      showWarningDialog: false,
    }));
  };

  const onOpenWonDialog = () => {
    setState((v) => ({
      ...v,
      showWonDialog: true,
    }));
  };

  const onCloseWonDialog = () => {
    dispatch(clearMomentumState(true));
    setState((v) => ({
      ...v,
      showWonDialog: false,
    }));
  };

  const onOpenStartDialog = () => {
    setState((v) => ({
      ...v,
      showStartDialog: true,
    }));
  };
  const onCloseStartDialog = () => {
    dispatch(setMomentumSource(''));
    setState((v) => ({
      ...v,
      showStartDialog: false,
    }));
    triggerGTMEvent({ event: 'tw-main-screen' });
  };

  const onSwitchToWidget = (e: React.MouseEvent<HTMLElement>) => {
    dispatch(setInGameHeaderSelection(''));
  };

  React.useEffect(() => {
    if (prize?.uuid && source !== 'ws') {
      triggerGTMEvent({ event: 'tw-main-screen' });
    }
  }, []); // eslint-disable-line

  const toFixedWithoutZeros = (num: number, precision: number) => `${Number.parseFloat(num.toFixed(precision))}`;

  const closeGame = () => {
    triggerGTMEvent({ event: 'tw-click-close' });

    exitSlotGame(navigate);
  };

  const showDeposit = (e: any) => {
    if (e) e.stopPropagation();
    triggerGTMEvent({ event: 'tw-deposit-click' });
    const loc = encodeURIComponent(window.location.pathname);
    navigate(`/deposit?from_game=${loc}`);
  };

  const onCancelBonus = () => {
    if (momentumPrizeLoading) return;
    dispatch(forfeitMomentumBonus(prize?.uuid ?? 0));
    triggerGTMEvent({ event: 'tw-info-cancel-bonus' });
  };

  const starProgress = momentumProgressDetails(state.prize?.wager_progress ?? 0, starThresholds);

  const stars = [...Array(starThresholds.length).keys()].map((index) => {
    let isLuckyStar = false;

    let animate = false;
    const active = index < state.starProgress;

    if (prize?.lucky_star?.star_index === index) {
      isLuckyStar = true;
      animate = true;
    }

    if (
      active &&
      prize?.lucky_star_prize?.reward_type === 'INSTANT_COMPLETE' &&
      prize?.lucky_star?.star_index < index
    ) {
      animate = true;
    }

    return {
      active: active,
      isLuckyStar: isLuckyStar,
      animate: animate,
    };
  });

  const finishAnimateStars = () => {
    setState((v) => {
      const newV = cloneDeep(v);
      newV.prize.status = 'wagered';
      newV.showWonDialog = true;
      return newV;
    });
  };

  const animateStars = () => {
    const animate = () => {
      setState((v) => {
        const newV = cloneDeep(v);
        const currentProgress = newV.prize?.wager_progress ?? 0;
        let nextProgress = 0;
        let currentStarIndex = 0;

        while (nextProgress < currentProgress && currentStarIndex < starThresholds.length) {
          nextProgress += starThresholds[currentStarIndex] ?? 0;
          currentStarIndex++;
        }

        newV.prize.wager_progress = nextProgress + 1 <= 100 ? nextProgress + 1 : 100;
        newV.starProgress = currentStarIndex;
        newV.skipAnimation = true;

        if (newV.prize.wager_progress >= 100) {
          setTimeout(() => {
            finishAnimateStars();
          }, 700);
        } else {
          setTimeout(animate, 700);
        }
        return newV;
      });
    };
    animate();
  };

  const onCloseLuckyStarAwardDialog = React.useCallback(() => {
    dispatch(setMomentumPrizeShown(true));

    setState((v) => {
      const newV = cloneDeep(v);
      if (v.prize?.lucky_star_prize?.reward_type === 'REWARD_INCREASE') {
        newV.startLuckyStarCounter = true;
      }

      if (v.prize?.lucky_star_prize?.reward_type === 'INSTANT_COMPLETE') {
        newV.skipAnimation = true;
        setTimeout(animateStars, 500);
      }

      newV.showLuckyStarAwardDialog = false;

      return newV;
    });
  }, []); // eslint-disable-line

  const value = toFixedWithoutZeros((prize?.reward?.value || 0) / 100, 2);
  const currency = formatCurrency(prize?.reward?.currency || '');
  const progress = starProgress?.progress ?? 0;

  if ((starProgress?.stars ?? 0) - state.starProgress > 1) {
    starProgress.stars = state.starProgress + 1;
  }

  const contextValue = {
    state,
    handleExpired,
    handleProgressAnimationEnd,
    onShowInfoDialog: trackFunction(onShowInfoDialog, {
      feature_used: 'turboCashInfoShow',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),

    onCloseInfoDialog: trackFunction(onCloseInfoDialog, {
      feature_used: 'turboCashInfoClose',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),

    onOpenWonDialog,
    onCloseWonDialog: trackFunction(onCloseWonDialog, {
      feature_used: 'turboCashWinDialogClose',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),
    onOpenStartDialog,
    onCloseStartDialog: trackFunction(onCloseStartDialog, {
      feature_used: 'turboCashStartDialogClose',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),
    onCancelBonus: trackFunction(onCancelBonus, {
      feature_used: 'turboCashCancelBonus',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),
    toFixedWithoutZeros,
    closeGame: trackFunction(closeGame, {
      feature_used: 'turboCashClose',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),

    onCloseLuckyStarAwardDialog: trackFunction(onCloseLuckyStarAwardDialog, {
      feature_used: 'turboCashLuckyStarClose',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),

    showDeposit: trackFunction(showDeposit, {
      feature_used: 'turboCashDeposit',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),
    starProgress,
    stars,
    value,
    currency,
    progress,
    isDesktop,
    isNoAnim,
    onSwitchToWidget: trackFunction(onSwitchToWidget, {
      feature_used: 'turboCashWidgetSwitch',
      feature_category: FeatureCategory.MomentumTurboCash,
      feature_subcategory: FeatureSubcategory.SystemEvent,
      feature_location: FeatureLocation.InGameNotification,
    }),
    momentumPrizeLoading,
    counterId: counterId.current,
  };

  try {
    if (contextValue.state.showWonDialog) {
      if (contextValue.state.prize?.lucky_star_prize?.reward_type === 'REWARD_INCREASE') {
        contextValue.value = toFixedWithoutZeros(
          (prize?.reward?.value || 0) / 100 + (contextValue.state.prize?.lucky_star_prize?.reward_value || 0),
          2,
        );
      }
    }
  } catch (e) {
    /* */
  }

  //console.log('MomentumGameHeader[contextValue]: ', contextValue);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default MomentumGameHeader;
